// ===========================================================================
// ReHome — Data Mutations
// Insert/Update/Delete helpers for Supabase. Graceful fallback for dev.
// ===========================================================================

import { supabase, isSupabaseConfigured } from './supabase';
import { toast } from './ui-utils';
import { toCamelCase } from './hooks';
import { ageFromBirthDate } from './types';
import { resizeDataUrl } from './imageResize';
import { PUBLIC_BUCKET, PRIVATE_BUCKET, isPublicPhotoRef, publicUrlToStoragePath } from './animalPhotos';
import type { Animal, AdoptionLead, Donation, SupporterSubscription, VaccinationMap, HeartwormResult } from './types';

const isDev = () => {
  try { return (import.meta as any).env?.DEV; } catch { return false; }
};

// ═══════════════════════════════════════════════════════════════════════════
// Client diagnostics — server-side only logging (never shown to the user)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Logs an operational diagnostic (e.g. "no Google Maps API key configured")
 * via the `log_client_diagnostic` Postgres RPC — visible in Supabase's Log
 * Explorer (Postgres Logs), never rendered in the UI. Best-effort: failures
 * are swallowed so a logging hiccup can never block the user's workflow.
 */
export const logClientDiagnostic = async (source: string, message: string): Promise<void> => {
  if (!isSupabaseConfigured()) {
    if (isDev()) console.warn(`[client-diagnostic:${source}]`, message);
    return;
  }
  try {
    await supabase.rpc('log_client_diagnostic', { p_source: source, p_message: message });
  } catch {
    // Best-effort — a diagnostic log failing must never block the UI.
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Animals
// ═══════════════════════════════════════════════════════════════════════════

export interface CreateAnimalInput {
  /** Optional client-pre-generated UUID — lets photo uploads target the final storage path before the row exists. */
  id?: string;
  shelterId: string;
  name: string;
  species: string;
  breed: string | null;
  age: number | null;
  gender: string | null;
  traits: string[];
  bio: string | null;
  status: string;
  intakeType: string | null;
  intakeDate?: string;
  foundLocation?: { lat: number; lng: number; address?: string; foundDate?: string } | null;
  strayHoldEndDate?: string | null;
  microchipId?: string | null;
  chipProvider?: string | null;
  chipStatus?: string | null;
  weightKg?: number | null;
  color?: string | null;
  isVaccinated?: boolean | null;
  isNeutered?: boolean | null;
  isDewormed?: boolean | null;
  /**
   * Becsült születési dátum. Ha meg van adva, EBBŐL származik az `age` —
   * a kettőt sosem a hívónak kell szinkronban tartania (l. lentebb).
   */
  birthDate?: string | null;
  /** Részletes oltási állapot (l. VACCINATIONS a types.ts-ben). */
  vaccinations?: VaccinationMap | null;
  heartwormTest?: HeartwormResult | null;
  heartwormTestedAt?: string | null;
  imageUrl?: string | null;
  gallery?: string[] | null;
  /** Privát képek tároló-útvonalai. Feltöltéskor MINDEN kép ide kerül. */
  privateGallery?: string[] | null;
  requiredAdopterFields?: string[] | null;
  adoptionFee?: number | null;
}

/**
 * Insert a new animal into Supabase.
 * Returns the created animal or null on failure.
 */
/**
 * Az `age` a születési dátumból származik, ha az ismert.
 *
 * Két forrás ugyanarra az adatra előbb-utóbb szétcsúszik. A születési dátum a
 * pontosabb (és nem avul el), az `age` viszont marad, mert szűrés és index
 * épül rá (`idx_animals_search`). Ezért itt, EGY helyen vezetjük le — a
 * hívóknak nem kell tudniuk róla.
 */
const resolveAge = (input: { age?: number | null; birthDate?: string | null }): number | null => {
  const derived = ageFromBirthDate(input.birthDate);
  return derived ?? (input.age ?? null);
};

export const createAnimal = async (input: CreateAnimalInput): Promise<Animal | null> => {
  if (!isSupabaseConfigured()) {
    toast.info('Supabase nincs konfigurálva — az állat helyileg került mentésre (demo mód).');
    // Return a mock animal for demo
    return {
      id: input.id || `demo-${Date.now()}`,
      shelterId: input.shelterId || '',
      name: input.name,
      species: input.species as any,
      breed: input.breed || null,
      age: resolveAge(input),
      gender: input.gender as any || null,
      weightKg: input.weightKg || null,
      traits: input.traits || [],
      bio: input.bio || null,
      imageUrl: input.imageUrl || null,
      gallery: input.gallery || [],
      privateGallery: input.privateGallery || [],
      intakeDate: input.intakeDate || new Date().toISOString().split('T')[0],
      intakeType: input.intakeType as any || null,
      foundLocation: input.foundLocation ? {
        ...input.foundLocation,
        foundDate: input.foundLocation.foundDate || new Date().toISOString().split('T')[0],
      } : null,
      strayHoldEndDate: input.strayHoldEndDate || null,
      status: input.status as any,
      microchipId: input.microchipId || null,
      chipProvider: input.chipProvider as any || null,
      chipStatus: input.chipStatus as any || null,
      color: input.color || null,
      isVaccinated: input.isVaccinated ?? null,
      isNeutered: input.isNeutered ?? null,
      isDewormed: input.isDewormed ?? null,
      birthDate: input.birthDate ?? null,
      vaccinations: input.vaccinations ?? {},
      heartwormTest: input.heartwormTest ?? null,
      heartwormTestedAt: input.heartwormTest ? (input.heartwormTestedAt ?? null) : null,
      adoptionDate: null,
      adopterId: null,
      adoptionFee: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  try {
    const { data, error } = await supabase
      .from('animals')
      .insert({
        ...(input.id ? { id: input.id } : {}),
        shelter_id: input.shelterId,
        name: input.name,
        species: input.species,
        breed: input.breed,
        age: resolveAge(input),
        birth_date: input.birthDate ?? null,
        gender: input.gender,
        traits: input.traits,
        bio: input.bio,
        status: input.status,
        intake_type: input.intakeType,
        intake_date: input.intakeDate || new Date().toISOString().split('T')[0],
        // Pass the plain object for this jsonb column — supabase-js already
        // JSON-serializes the whole request body. JSON.stringify()-ing it
        // here first would double-encode it into a JSON *string* value
        // (valid JSON, but then animal.foundLocation.lat reads back as
        // undefined instead of a number, since foundLocation itself becomes
        // a string, not an object).
        found_location: input.foundLocation || null,
        stray_hold_end: input.strayHoldEndDate,
        microchip_id: input.microchipId,
        chip_provider: input.chipProvider,
        chip_status: input.chipStatus,
        color: input.color ?? null,
        is_vaccinated: input.isVaccinated ?? null,
        is_neutered: input.isNeutered ?? null,
        is_dewormed: input.isDewormed ?? null,
        vaccinations: input.vaccinations ?? {},
        heartworm_test: input.heartwormTest ?? null,
        // A DB megszorítása szerint dátum csak eredménnyel együtt állhat.
        heartworm_tested_at: input.heartwormTest ? (input.heartwormTestedAt ?? null) : null,
        weight_kg: input.weightKg,
        image_url: input.imageUrl,
        gallery: input.gallery ?? [],
        private_gallery: input.privateGallery ?? [],
        required_adopter_fields: input.requiredAdopterFields ?? [],
        adoption_fee: input.adoptionFee ?? null,
      })
      .select()
      .single();

    if (error) throw error;
    toast.success(`${input.name} sikeresen felvéve!`);
    return toCamelCase<Animal>(data);
  } catch (err: any) {
    if (isDev()) console.error('[createAnimal]', err);
    toast.error(`Hiba a mentés során: ${err.message || 'Ismeretlen hiba'}`);
    return null;
  }
};

/**
 * Update an existing animal's data. Only fields present in the input are updated.
 * Returns the updated animal or null on failure.
 */
export const updateAnimal = async (
  animalId: string,
  input: Partial<CreateAnimalInput>
): Promise<Animal | null> => {
  if (!isSupabaseConfigured()) {
    toast.info('Demo mód — állat adatai helyileg frissítve.');
    return null;
  }

  try {
    // Build the update payload (camelCase → snake_case)
    const update: Record<string, any> = { updated_at: new Date().toISOString() };

    if (input.name !== undefined) update.name = input.name;
    if (input.species !== undefined) update.species = input.species;
    if (input.breed !== undefined) update.breed = input.breed;
    if (input.age !== undefined) update.age = input.age;
    if (input.gender !== undefined) update.gender = input.gender;
    if (input.weightKg !== undefined) update.weight_kg = input.weightKg;
    if (input.traits !== undefined) update.traits = input.traits;
    if (input.bio !== undefined) update.bio = input.bio;
    if (input.status !== undefined) update.status = input.status;
    if (input.intakeType !== undefined) update.intake_type = input.intakeType;
    if (input.intakeDate !== undefined) update.intake_date = input.intakeDate;
    if (input.foundLocation !== undefined) {
      // See the matching comment in createAnimal — pass the plain object,
      // not a stringified one, for this jsonb column.
      update.found_location = input.foundLocation || null;
    }
    if (input.strayHoldEndDate !== undefined) update.stray_hold_end = input.strayHoldEndDate;
    if (input.microchipId !== undefined) update.microchip_id = input.microchipId;
    if (input.chipProvider !== undefined) update.chip_provider = input.chipProvider;
    if (input.chipStatus !== undefined) update.chip_status = input.chipStatus;
    if (input.color !== undefined) update.color = input.color;
    if (input.isVaccinated !== undefined) update.is_vaccinated = input.isVaccinated;
    if (input.isNeutered !== undefined) update.is_neutered = input.isNeutered;
    if (input.isDewormed !== undefined) update.is_dewormed = input.isDewormed;
    if (input.vaccinations !== undefined) update.vaccinations = input.vaccinations ?? {};
    if (input.heartwormTest !== undefined) {
      update.heartworm_test = input.heartwormTest;
      // Eredmény törlésekor a dátum sem maradhat (DB CHECK).
      if (!input.heartwormTest) update.heartworm_tested_at = null;
    }
    if (input.heartwormTestedAt !== undefined && input.heartwormTest !== null) {
      update.heartworm_tested_at = input.heartwormTestedAt;
    }
    if (input.birthDate !== undefined) {
      update.birth_date = input.birthDate;
      // A születési dátum megváltozásával az `age` is elavul.
      const derived = ageFromBirthDate(input.birthDate);
      if (derived !== null) update.age = derived;
    }
    if (input.imageUrl !== undefined) update.image_url = input.imageUrl;
    if (input.gallery !== undefined) update.gallery = input.gallery;
    if (input.privateGallery !== undefined) update.private_gallery = input.privateGallery;
    if (input.requiredAdopterFields !== undefined) update.required_adopter_fields = input.requiredAdopterFields;

    const { data, error } = await supabase
      .from('animals')
      .update(update)
      .eq('id', animalId)
      .select()
      .maybeSingle();

    if (error) throw error;
    if (!data) {
      toast.error('Nincs jogosultságod az állat adatainak módosításához, vagy az állat nem található.');
      return null;
    }
    toast.success(`${data.name || 'Állat'} adatai frissítve!`);
    return toCamelCase<Animal>(data);
  } catch (err: any) {
    if (isDev()) console.error('[updateAnimal]', err);
    toast.error(`Hiba a mentés során: ${err.message || 'Ismeretlen hiba'}`);
    return null;
  }
};

/**
 * Manually change an animal's status (e.g. StrayHold → Available once the
 * legal hold expires — there is no automated job for this). Runs through
 * the `change_animal_status` RPC so the change is atomically logged in
 * `animal_status_changes` with the acting user, never a plain `updateAnimal`
 * call — see 030_animal_status_log.sql.
 */
export const changeAnimalStatus = async (
  animalId: string,
  newStatus: string,
  reason?: string
): Promise<{ success: boolean; oldStatus?: string; newStatus?: string; error?: string }> => {
  if (!isSupabaseConfigured()) {
    toast.success('Demo mód: állapot módosítva!');
    return { success: true, newStatus };
  }

  try {
    const { data, error } = await supabase.rpc('change_animal_status', {
      p_animal_id: animalId,
      p_new_status: newStatus,
      p_reason: reason?.trim() || null,
    });

    if (error) throw error;
    const result = data as any;
    if (result.success) {
      toast.success('Állapot sikeresen módosítva!');
    } else {
      toast.error(result.error || 'Hiba a státusz módosítása során.');
    }
    return result;
  } catch (err: any) {
    if (isDev()) console.error('[changeAnimalStatus]', err);
    toast.error(`Hiba: ${err.message}`);
    return { success: false, error: err.message };
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Animal Intake Records — staff-only sensitive intake data
// ═══════════════════════════════════════════════════════════════════════════

import type { AnimalIntakeRecord } from './types';

export interface UpsertAnimalIntakeRecordInput {
  circumstances?: string | null;
  chipOwnerInfo?: string | null;
  intakeReferenceNumber?: string | null;
  finderName?: string | null;
  finderPhone?: string | null;
}

/**
 * Upsert the staff-only intake record for an animal (circumstances, chip
 * owner info, reference number, finder contact). Never readable from the
 * public animals table — see 028_intake_refinement.sql.
 */
export const upsertAnimalIntakeRecord = async (
  animalId: string,
  input: UpsertAnimalIntakeRecordInput
): Promise<AnimalIntakeRecord | null> => {
  if (!isSupabaseConfigured()) {
    toast.info('Demo mód — bekerülési részletek helyileg mentve.');
    return {
      animalId,
      circumstances: input.circumstances ?? null,
      chipOwnerInfo: input.chipOwnerInfo ?? null,
      intakeReferenceNumber: input.intakeReferenceNumber ?? null,
      finderName: input.finderName ?? null,
      finderPhone: input.finderPhone ?? null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  try {
    const snakeInput: Record<string, any> = { animal_id: animalId };
    if (input.circumstances !== undefined) snakeInput.circumstances = input.circumstances;
    if (input.chipOwnerInfo !== undefined) snakeInput.chip_owner_info = input.chipOwnerInfo;
    if (input.intakeReferenceNumber !== undefined) snakeInput.intake_reference_number = input.intakeReferenceNumber;
    if (input.finderName !== undefined) snakeInput.finder_name = input.finderName;
    if (input.finderPhone !== undefined) snakeInput.finder_phone = input.finderPhone;

    const { data, error } = await supabase
      .from('animal_intake_records')
      .upsert(snakeInput, { onConflict: 'animal_id' })
      .select()
      .single();

    if (error) throw error;
    return toCamelCase<AnimalIntakeRecord>(data);
  } catch (err: any) {
    if (isDev()) console.error('[upsertAnimalIntakeRecord]', err);
    toast.error(`Hiba a bekerülési adatok mentése során: ${err.message}`);
    return null;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Animal Medical Events — staff-only health log (vaccination, deworming, ...)
// ═══════════════════════════════════════════════════════════════════════════

import type { AnimalMedicalEvent, MedicalEventType } from './types';

export interface CreateAnimalMedicalEventInput {
  animalId: string;
  eventType: MedicalEventType;
  title: string;
  eventDate: string;
  notes?: string | null;
  veterinarian?: string | null;
  isRecurring?: boolean;
  recurrenceIntervalDays?: number | null;
  nextDueDate?: string | null;
  customLeadDays?: number | null;
  /**
   * Opcionális: az állat profilján lévő egészségügyi jelölő (Oltott /
   * Parazitamentes / Ivartalanítva) egyidejű beállítása.
   *
   * SZÁNDÉKOSAN a hívó dönti el, nem a típusból vezetjük le automatikusan:
   * egy elhúzódó parazitakezelés első kezelése után az állat még nem
   * "parazitamentes", tehát a menhelyi dolgozónak kell megerősítenie
   * (l. AnimalMedicalPage megerősítő sávja).
   */
  animalFlag?: { column: 'is_vaccinated' | 'is_dewormed' | 'is_neutered'; value: boolean } | null;
  /**
   * Opcionális: MELYIK konkrét oltás állapotát állítsa át a bejegyzés
   * (l. VACCINATIONS). Az `animalFlag` az összefoglaló "Oltott" jelölőt
   * kezeli; ez a részletes térkép egy kulcsát.
   */
  vaccinationKey?: string | null;
}

/**
 * Logs a new medical event (one occurrence — e.g. this year's rabies shot).
 * Recurring events with a nextDueDate set up their own future reminder via
 * the check-medical-reminders cron; performing the next occurrence means
 * logging another event, not editing this one — see 034_animal_medical_care.sql.
 */
export const createAnimalMedicalEvent = async (
  input: CreateAnimalMedicalEventInput
): Promise<AnimalMedicalEvent | null> => {
  if (!isSupabaseConfigured()) {
    toast.info('Demo mód — egészségügyi esemény helyileg mentve.');
    return {
      id: `demo-medical-event-${Date.now()}`,
      animalId: input.animalId,
      eventType: input.eventType,
      title: input.title,
      eventDate: input.eventDate,
      notes: input.notes ?? null,
      veterinarian: input.veterinarian ?? null,
      isRecurring: input.isRecurring ?? false,
      recurrenceIntervalDays: input.recurrenceIntervalDays ?? null,
      nextDueDate: input.nextDueDate ?? null,
      customLeadDays: input.customLeadDays ?? null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  try {
    const { data, error } = await supabase
      .from('animal_medical_events')
      .insert({
        animal_id: input.animalId,
        event_type: input.eventType,
        title: input.title,
        event_date: input.eventDate,
        notes: input.notes ?? null,
        veterinarian: input.veterinarian ?? null,
        is_recurring: input.isRecurring ?? false,
        recurrence_interval_days: input.recurrenceIntervalDays ?? null,
        next_due_date: input.nextDueDate ?? null,
        custom_lead_days: input.customLeadDays ?? null,
      })
      .select()
      .single();

    if (error) throw error;

    // Az állatprofil jelölőjének frissítése, ha a dolgozó kérte. Külön
    // try/catch: a bejegyzés MÁR elmentődött, ezért egy itteni hiba nem
    // teheti sikertelenné az egész műveletet — csak jelezzük, hogy a jelölőt
    // kézzel kell átállítani.
    if (input.animalFlag) {
      const { error: flagError } = await supabase
        .from('animals')
        .update({ [input.animalFlag.column]: input.animalFlag.value, updated_at: new Date().toISOString() })
        .eq('id', input.animalId);

      if (flagError) {
        if (isDev()) console.error('[createAnimalMedicalEvent] flag update', flagError);
        toast.error('A bejegyzés elmentve, de az állat profiljának frissítése nem sikerült — állítsd át kézzel.');
        return toCamelCase<AnimalMedicalEvent>(data);
      }
    }

    // A részletes oltási térkép EGY kulcsa — szerveroldali jsonb-merge-dzsel,
    // hogy egy párhuzamos szerkesztés ne írja felül az egész térképet.
    if (input.vaccinationKey) {
      const { error: vaccError } = await supabase.rpc('set_animal_vaccination', {
        p_animal_id: input.animalId,
        p_key: input.vaccinationKey,
        p_value: 'yes',
      });
      if (vaccError) {
        if (isDev()) console.error('[createAnimalMedicalEvent] vaccination', vaccError);
        toast.error('A bejegyzés elmentve, de az oltási állapot frissítése nem sikerült — állítsd át kézzel.');
        return toCamelCase<AnimalMedicalEvent>(data);
      }
    }

    toast.success(
      input.animalFlag || input.vaccinationKey
        ? 'Egészségügyi esemény rögzítve, az állat profilja frissítve!'
        : 'Egészségügyi esemény rögzítve!'
    );
    return toCamelCase<AnimalMedicalEvent>(data);
  } catch (err: any) {
    if (isDev()) console.error('[createAnimalMedicalEvent]', err);
    toast.error(`Hiba az esemény mentése során: ${err.message}`);
    return null;
  }
};

export interface UpdateAnimalMedicalEventInput {
  eventType?: MedicalEventType;
  title?: string;
  eventDate?: string;
  notes?: string | null;
  veterinarian?: string | null;
  isRecurring?: boolean;
  recurrenceIntervalDays?: number | null;
  nextDueDate?: string | null;
  customLeadDays?: number | null;
}

/**
 * Egy egészségügyi esemény / emlékeztető módosítása.
 *
 * Csak a ténylegesen megadott mezőket írja — így az emlékeztető-dialogból
 * érkező részleges szerkesztés nem nullázza ki azt, amit a felület meg sem
 * mutat (pl. az állatorvos nevét).
 *
 * Az ismétlődés kikapcsolásakor az esedékesség is törlődik: egy nem ismétlődő
 * eseményen ottfelejtett `next_due_date` továbbra is emlékeztetőként jelenne
 * meg a listában és a naptárban.
 */
export const updateAnimalMedicalEvent = async (
  eventId: string,
  input: UpdateAnimalMedicalEventInput
): Promise<boolean> => {
  if (!isSupabaseConfigured()) {
    toast.info('Demo mód — emlékeztető frissítve.');
    return true;
  }

  const update: Record<string, any> = {};
  if (input.eventType !== undefined) update.event_type = input.eventType;
  if (input.title !== undefined) update.title = input.title;
  if (input.eventDate !== undefined) update.event_date = input.eventDate;
  if (input.notes !== undefined) update.notes = input.notes;
  if (input.veterinarian !== undefined) update.veterinarian = input.veterinarian;
  if (input.recurrenceIntervalDays !== undefined) update.recurrence_interval_days = input.recurrenceIntervalDays;
  if (input.customLeadDays !== undefined) update.custom_lead_days = input.customLeadDays;
  if (input.nextDueDate !== undefined) update.next_due_date = input.nextDueDate;
  if (input.isRecurring !== undefined) {
    update.is_recurring = input.isRecurring;
    if (!input.isRecurring) {
      update.next_due_date = null;
      update.recurrence_interval_days = null;
    }
  }

  if (Object.keys(update).length === 0) return true;

  try {
    const { error } = await supabase
      .from('animal_medical_events')
      .update(update)
      .eq('id', eventId);
    if (error) throw error;
    toast.success('Emlékeztető frissítve.');
    return true;
  } catch (err: any) {
    if (isDev()) console.error('[updateAnimalMedicalEvent]', err);
    toast.error(`Hiba az emlékeztető mentése során: ${err.message}`);
    return false;
  }
};

/** Deletes a mistakenly-logged medical event. Cascades to its notification, if any. */
export const deleteAnimalMedicalEvent = async (eventId: string): Promise<boolean> => {
  if (!isSupabaseConfigured()) {
    toast.info('Demo mód — esemény törölve.');
    return true;
  }

  try {
    const { error } = await supabase.from('animal_medical_events').delete().eq('id', eventId);
    if (error) throw error;
    toast.success('Esemény törölve.');
    return true;
  } catch (err: any) {
    if (isDev()) console.error('[deleteAnimalMedicalEvent]', err);
    toast.error(`Hiba az esemény törlése során: ${err.message}`);
    return false;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Adoption Leads — CRM Pipeline
// ═══════════════════════════════════════════════════════════════════════════

export const updateLeadStatus = async (
  leadId: string,
  newStatus: string,
  notes?: string
): Promise<boolean> => {
  if (!isSupabaseConfigured()) {
    toast.info('Demo mód — státuszváltás szimulálva.');
    return true;
  }

  try {
    const update: Record<string, any> = { status: newStatus, updated_at: new Date().toISOString() };
    if (notes) update.notes = notes;

    const { error } = await supabase
      .from('adoption_leads')
      .update(update)
      .eq('id', leadId);

    if (error) throw error;
    toast.success('Státusz frissítve!');
    return true;
  } catch (err: any) {
    if (isDev()) console.error('[updateLeadStatus]', err);
    toast.error(`Hiba: ${err.message}`);
    return false;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Post-Adoption Updates
// ═══════════════════════════════════════════════════════════════════════════

export interface CreateCheckInInput {
  animalId: string;
  type: string; // '3_Day' | '3_Week' | '3_Month' | 'Spontaneous'
  imageUrl: string; // base64 data URL from file input
  /**
   * Opcionális GPS pozíció. Az örökbefogadói helyzetjelentés űrlapról
   * SZÁNDÉKOSAN kikerült: az utókövetéshez nincs szükség az örökbefogadó
   * tartózkodási helyére, a menhely a fotóból és a szöveges beszámolóból
   * kap képet. Az oszlop a régi rekordok miatt marad a sémában.
   */
  gpsLocation?: { lat: number; lng: number } | null;
  mood: string | null;
  notes: string | null;
}

/** Convert a base64 data URL (from a camera/file input) into a Blob. */
/**
 * Minden feltöltési útvonal EGYETLEN közös előkészítője: átméretezés (a
 * hosszabbik él max. 720 px) + blobbá alakítás.
 *
 * Szándékosan itt van, és nem a fájlválasztó komponensben: így az admin
 * bekerülési űrlap, az állat adatlap és a platform check-in mind ugyanazt
 * kapja, és egy új feltöltési pont sem tudja véletlenül kihagyni.
 */
const prepareImageForUpload = async (dataUrl: string): Promise<{ blob: Blob; mime: string }> =>
  dataUrlToBlob(await resizeDataUrl(dataUrl));

const dataUrlToBlob = (dataUrl: string): { blob: Blob; mime: string } => {
  const base64 = dataUrl.split(',')[1];
  const mime = dataUrl.match(/data:(.*?);/)?.[1] || 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return { blob: new Blob([bytes], { type: mime }), mime };
};

/**
 * Upload a check-in photo to the PRIVATE rehome_private bucket — these are
 * personal photos tied to precise GPS coordinates (post_adoption_updates),
 * never public (see 031_private_checkins_storage.sql / compliance.md 4.1).
 * Returns the storage PATH (not a URL — the object has no public URL).
 * Use getCheckInPhotoUrl() to resolve a short-lived signed URL for display.
 */
const uploadCheckInPhoto = async (animalId: string, dataUrl: string): Promise<string | null> => {
  if (!isSupabaseConfigured()) return dataUrl; // demo mode: keep the data URL as-is

  try {
    const { blob, mime } = await prepareImageForUpload(dataUrl);
    const path = `checkins/${animalId}/${Date.now()}.jpg`;

    const { error } = await supabase.storage
      .from('rehome_private')
      .upload(path, blob, { contentType: mime, upsert: true });

    if (error) throw error;
    return path;
  } catch (err: any) {
    if (isDev()) console.error('[uploadCheckInPhoto]', err);
    return null;
  }
};

/**
 * Resolve a short-lived signed URL for a check-in photo stored at `path`
 * (post_adoption_updates.image_url) in the private bucket. RLS restricts
 * this to the uploading adopter, the animal's shelter staff, and
 * super_admin — anyone else gets an error and this returns null.
 */
export const getCheckInPhotoUrl = async (path: string, expiresInSeconds = 300): Promise<string | null> => {
  if (!path) return null;
  if (!isSupabaseConfigured() || path.startsWith('data:')) return path; // demo mode / already a data URL

  try {
    const { data, error } = await supabase.storage
      .from('rehome_private')
      .createSignedUrl(path, expiresInSeconds);

    if (error) throw error;
    return data?.signedUrl || null;
  } catch (err: any) {
    if (isDev()) console.error('[getCheckInPhotoUrl]', err);
    return null;
  }
};

/**
 * Állatfotó feltöltése — MINDIG a privát tárolóba.
 *
 * A kép alapértelmezetten privát; publikussá a menhely teszi, egyenként
 * (l. `setAnimalPhotoVisibility`). Ezért nem publikus URL-t ad vissza, hanem
 * a tároló-ÚTVONALAT: ez kerül az `animals.private_gallery` tömbbe, és ez az,
 * amiből aláírt link kérhető.
 *
 * A `rehome_public` bucket olvasási policy-ja `TO public` — ami oda felkerül,
 * azt bárki letöltheti, aki ismeri az URL-t. Egy "később majd privátra
 * állítjuk" feltöltés tehát már a feltöltés pillanatában nyilvános lenne.
 */
/**
 * Az `animals/{animalId}/…` alakú tároló-útvonal egy meglévő fájlhoz.
 *
 * A fájlnevet megtartja (az utolsó `/` utáni részt), de a mappát az állathoz
 * igazítja. Ha az útvonal már kanonikus, változatlanul tér vissza — így egy
 * ismételt mozgatás nem ágyaz be újabb szintet.
 */
export const canonicalAnimalPhotoPath = (animalId: string, storagePath: string): string => {
  const fileName = storagePath.split('/').pop() || `${Date.now()}.jpg`;
  const canonicalPrefix = `animals/${animalId}/`;
  return storagePath.startsWith(canonicalPrefix) ? storagePath : `${canonicalPrefix}${fileName}`;
};

export const uploadAnimalImage = async (animalId: string, dataUrl: string, index = 0): Promise<string | null> => {
  if (!isSupabaseConfigured()) return dataUrl; // demo mód: marad a data URL

  try {
    const { blob, mime } = await prepareImageForUpload(dataUrl);
    const path = `animals/${animalId}/${Date.now()}-${index}.jpg`;

    const { error } = await supabase.storage
      .from(PRIVATE_BUCKET)
      .upload(path, blob, { contentType: mime, upsert: true });

    if (error) throw error;
    return path;
  } catch (err: any) {
    if (isDev()) console.error('[uploadAnimalImage]', err);
    return null;
  }
};

/**
 * Frissen feltöltött kép hozzáfűzése az állathoz (mindig privátként).
 *
 * Külön RPC, és nem az űrlap `updateAnimal` hívásának része: így a lista
 * a szerveren, a tényleges aktuális értékre bővül. Egy kliensoldali
 * "beolvas – hozzáfűz – visszaír" eltüntetné annak a kollégának a képét,
 * aki közben töltött fel egyet.
 */
export const addAnimalPhoto = async (animalId: string, dataUrl: string, index = 0): Promise<string | null> => {
  const ref = await uploadAnimalImage(animalId, dataUrl, index);
  if (!ref) {
    toast.error('A kép feltöltése nem sikerült.');
    return null;
  }
  if (!isSupabaseConfigured()) return ref;

  const { error } = await supabase.rpc('add_animal_private_photo', {
    p_animal_id: animalId,
    p_path: ref,
  });
  if (error) {
    if (isDev()) console.error('[addAnimalPhoto]', error);
    toast.error('A kép feltöltődött, de nem sikerült az állathoz rendelni.');
    return null;
  }
  return ref;
};

export interface IntakePhoto {
  dataUrl: string;
  /** Publikussá tegyük-e a mentés után. A feltöltés mindig privátba megy. */
  isPublic: boolean;
}

/**
 * A bekerüléskor kiválasztott fotók feltöltése és az állathoz rendelése.
 *
 * ── A sorrend nem kozmetika ────────────────────────────────────────────────
 * A fotók CSAK az állat sorának létrejötte UTÁN tölthetők fel. A privát
 * tároló beszúrási szabálya (`rehome_private_insert_animals`) azt nézi, hogy
 * az `animals/{id}/…` útvonalban szereplő azonosítóhoz tartozik-e a hívó
 * menhelyének állata — nem létező sorra ez hamis, tehát az RLS elutasítja a
 * feltöltést. A korábbi kód még a publikus tárolóba töltött, aminek a
 * szabálya nem nézett állatot, ezért működött az előre generált azonosítóval;
 * a privát tárolóra váltás után ez néma hibává vált.
 *
 * A publikussá tétel is ide tartozik: a `set_animal_photo_visibility` az
 * `animals` sorát írja, tehát szintén létező sort igényel.
 */
export const attachIntakePhotos = async (
  animalId: string,
  photos: IntakePhoto[]
): Promise<{ uploaded: number; published: number }> => {
  let uploaded = 0;
  let published = 0;

  for (let i = 0; i < photos.length; i++) {
    const ref = await addAnimalPhoto(animalId, photos[i].dataUrl, i);
    if (!ref) continue;
    uploaded++;

    if (photos[i].isPublic) {
      const publicRef = await setAnimalPhotoVisibility(animalId, ref, true);
      if (publicRef) published++;
    }
  }

  return { uploaded, published };
};

/**
 * Egy kép láthatóságának váltása. Visszaadja az ÚJ hivatkozást (publikus URL
 * vagy privát útvonal), vagy `null`-t, ha nem sikerült.
 *
 * A sorrend fontos: előbb a tároló-mozgatás, utána a nyilvántartás. Fordítva
 * egy sikeres adatbázis-írás után egy elbukott mozgatás olyan URL-t hagyna a
 * publikus listában, ami mögött nincs fájl — a látogató törött képet látna.
 * Ha a mozgatás után bukik az RPC, VISSZAMOZGATJUK a képet, hogy a tároló és
 * az adatbázis ne csússzon szét.
 */
export const setAnimalPhotoVisibility = async (
  animalId: string,
  ref: string,
  makePublic: boolean
): Promise<string | null> => {
  if (!isSupabaseConfigured()) return ref; // demo mód: nincs mit mozgatni

  const fromBucket = makePublic ? PRIVATE_BUCKET : PUBLIC_BUCKET;
  const toBucket = makePublic ? PUBLIC_BUCKET : PRIVATE_BUCKET;

  // Publikus képnél a hivatkozás URL — a tároló-útvonalat vissza kell fejteni.
  const fromPath = makePublic ? ref : publicUrlToStoragePath(ref);
  if (!fromPath) {
    toast.error('Ez a kép nem a ReHome tárolójából származik, a láthatósága nem módosítható.');
    return null;
  }

  // A CÉL mindig kanonikus útvonal: `animals/{animalId}/…`.
  //
  // A régebbi képek egy szinttel feljebb, `animals/{fájlnév}` néven állnak
  // (így tölti fel őket a setup-dev.ps1). Ha a mozgatás megtartaná az
  // útvonalat, a kép a másik tárolóban is ott maradna — és mivel a
  // jogosultság-ellenőrzés gyors ága az azonosítót az útvonalból olvassa, a
  // visszaút megint csak a lassabb, lista-alapú ágon menne. Célként
  // normalizálva minden kép az ELSŐ láthatóság-váltáskor a helyére kerül.
  const toPath = canonicalAnimalPhotoPath(animalId, fromPath);

  try {
    const { error: moveError } = await supabase.storage
      .from(fromBucket)
      .move(fromPath, toPath, { destinationBucket: toBucket });
    if (moveError) throw moveError;

    const newRef = makePublic
      ? supabase.storage.from(PUBLIC_BUCKET).getPublicUrl(toPath).data.publicUrl
      : toPath;

    const { error: rpcError } = await supabase.rpc('set_animal_photo_visibility', {
      p_animal_id: animalId,
      p_from: ref,
      p_to: newRef,
      p_public: makePublic,
    });

    if (rpcError) {
      // Kompenzáció: a fájl visszakerül oda, ahol az adatbázis szerint van.
      await supabase.storage.from(toBucket).move(toPath, fromPath, { destinationBucket: fromBucket });
      throw rpcError;
    }

    toast.success(makePublic ? 'A kép mostantól látható a platformon.' : 'A kép mostantól csak a menhely számára látható.');
    return newRef;
  } catch (err: any) {
    if (isDev()) console.error('[setAnimalPhotoVisibility]', err);
    // A jogosultsági elutasítás a leggyakoribb ok, és önmagában a "nem
    // sikerült" semmit nem mond arról, mit tehet a felhasználó.
    const denied = /row-level security|not authorized|Unauthorized|403/i.test(err?.message || '');
    toast.error(denied
      ? 'A láthatóság módosításához nincs jogosultságod ehhez a képhez.'
      : 'A láthatóság módosítása nem sikerült.');
    return null;
  }
};

/**
 * Kép végleges eltávolítása: kikerül a listákból ÉS a tárolóból is.
 *
 * A nyilvántartás megy előbb: ha a tároló-törlés bukik, egy hivatkozás nélküli
 * árva fájl marad (nem látszik sehol), ami sokkal kevésbé zavaró, mint egy
 * törött kép a felületen.
 */
export const removeAnimalPhoto = async (animalId: string, ref: string): Promise<boolean> => {
  if (!isSupabaseConfigured()) return true;

  try {
    const { error } = await supabase.rpc('remove_animal_photo', {
      p_animal_id: animalId,
      p_ref: ref,
    });
    if (error) throw error;

    const isPublic = isPublicPhotoRef(ref);
    const path = isPublic ? publicUrlToStoragePath(ref) : ref;
    if (path) {
      await supabase.storage.from(isPublic ? PUBLIC_BUCKET : PRIVATE_BUCKET).remove([path]);
    }
    return true;
  } catch (err: any) {
    if (isDev()) console.error('[removeAnimalPhoto]', err);
    toast.error('A kép törlése nem sikerült.');
    return false;
  }
};

export const createCheckIn = async (input: CreateCheckInInput): Promise<boolean> => {
  // Upload photo to Supabase Storage (or keep data URL in demo mode)
  const uploadedUrl = await uploadCheckInPhoto(input.animalId, input.imageUrl);
  if (!uploadedUrl && isSupabaseConfigured()) {
    toast.error('A kép feltöltése nem sikerült.');
    return false;
  }

  if (!isSupabaseConfigured()) {
    toast.success('Check-in rögzítve (demo mód)!');
    return true;
  }

  // Get current user for adopter_id (required FK)
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    toast.error('A check-inhez be kell jelentkezned.');
    return false;
  }

  try {
    const { error } = await supabase
      .from('post_adoption_updates')
      .insert({
        animal_id: input.animalId,
        adopter_id: user.id,
        update_type: input.type,
        image_url: uploadedUrl,
        gps_lat: input.gpsLocation?.lat ?? null,
        gps_lng: input.gpsLocation?.lng ?? null,
        mood: input.mood,
        notes: input.notes,
        verified_by_shelter: false,
      });

    if (error) throw error;

    // Notify shelter about the new check-in
    try {
      const { data: animal } = await supabase
        .from('animals')
        .select('shelter_id, name')
        .eq('id', input.animalId)
        .single();

      if (animal) {
        await supabase.from('notifications').insert({
          shelter_id: animal.shelter_id,
          category: 'PostAdoption',
          severity: 'Medium',
          title: `Új check-in: ${animal.name}`,
          message: `Egy örökbefogadó friss check-int küldött ${animal.name} állathoz. Ellenőrizd az utókövetési dashboard-on.`,
        });
      }
    } catch {
      // Notification failure is non-critical — don't block the check-in
    }

    toast.success('Check-in sikeresen beküldve!');
    return true;
  } catch (err: any) {
    if (isDev()) console.error('[createCheckIn]', err);
    toast.error(`Hiba a check-in során: ${err.message}`);
    return false;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Post-Adoption Verification
// ═══════════════════════════════════════════════════════════════════════════

export const verifyPostAdoptionUpdate = async (
  updateId: string,
  verified: boolean
): Promise<boolean> => {
  if (!isSupabaseConfigured()) {
    toast.info('Demo mód — visszaigazolás szimulálva.');
    return true;
  }

  try {
    const { error } = await supabase
      .from('post_adoption_updates')
      .update({ verified_by_shelter: verified })
      .eq('id', updateId);

    if (error) throw error;
    toast.success(verified ? 'Visszaigazolva!' : 'Visszaigazolás visszavonva.');
    return true;
  } catch (err: any) {
    console.error('[verifyPostAdoptionUpdate]', err);
    toast.error(`Hiba: ${err.message}`);
    return false;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Donations & Campaigns
// ═══════════════════════════════════════════════════════════════════════════

export interface CreateDonationInput {
  campaignId: string | null;
  amount: number;
  donorName?: string | null;
  donorEmail?: string | null;
  isAnonymous?: boolean;
  message?: string | null;
  shelterId: string | null;
}

/**
 * Create a donation record + financial transaction. Demo mode: saves to DB.
 * Returns the created donation or null on failure.
 */
export const createDonation = async (input: CreateDonationInput): Promise<Donation | null> => {
  // Get current user if logged in
  let donorId: string | null = null;
  if (isSupabaseConfigured()) {
    const { data: { user } } = await supabase.auth.getUser();
    donorId = user?.id || null;
  }

  if (!isSupabaseConfigured()) {
    toast.success('Demo mód: Adomány rögzítve! Köszönjük a támogatást!');
    return {
      id: `demo-${Date.now()}`,
      campaignId: input.campaignId,
      donorId,
      amount: input.amount,
      donorName: input.donorName || null,
      donorEmail: input.donorEmail || null,
      isAnonymous: input.isAnonymous || false,
      stripePaymentId: 'demo_' + Date.now(),
      message: input.message || null,
      createdAt: new Date().toISOString(),
    };
  }

  try {
    // Pre-generate UUID so we don't need .select() after insert
    // (the donations_select_staff RLS JOINs shelter_staff which anon
    //  users cannot access, causing "permission denied for table shelter_staff")
    const donationId = crypto.randomUUID();
    const stripePaymentId = 'demo_' + Date.now();
    const now = new Date().toISOString();

    const { error } = await supabase
      .from('donations')
      .insert({
        id: donationId,
        campaign_id: input.campaignId,
        donor_id: donorId,
        amount: input.amount,
        donor_name: input.donorName,
        donor_email: input.donorEmail,
        is_anonymous: input.isAnonymous || false,
        stripe_payment_id: stripePaymentId,
        message: input.message,
      });

    if (error) throw error;

    // Insert financial transaction for bookkeeping.
    // shelter_id is NOT NULL in the schema — skip the transaction when the
    // donation has no shelter context instead of firing a doomed insert.
    if (input.shelterId) {
      const { error: txError } = await supabase.from('financial_transactions').insert({
        shelter_id: input.shelterId,
        date: now.split('T')[0],
        amount: input.amount,
        type: 'Income',
        category: 'Donation',
        donation_id: donationId,
        campaign_id: input.campaignId,
        description: input.message || 'Online adomány',
      });
      if (txError && isDev()) console.warn('[createDonation] Financial tx insert failed:', txError.message);
    }

    toast.success('Köszönjük a támogatást! Az adomány sikeresen rögzítve.');
    return {
      id: donationId,
      campaignId: input.campaignId,
      donorId,
      amount: input.amount,
      donorName: input.donorName || null,
      donorEmail: input.donorEmail || null,
      isAnonymous: input.isAnonymous || false,
      stripePaymentId: stripePaymentId,
      message: input.message || null,
      createdAt: now,
    };
  } catch (err: any) {
    // Do NOT fake success here — a failed insert must be visible to the user,
    // otherwise donations silently never reach the database.
    if (isDev()) console.error('[createDonation] DB insert failed:', err.message);
    toast.error(`Az adomány rögzítése nem sikerült: ${err.message || 'Ismeretlen hiba'}`);
    return null;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Volunteer Applications
// ═══════════════════════════════════════════════════════════════════════════

export interface CreateVolunteerApplicationInput {
  name: string;
  email: string;
  phone?: string | null;
  helpType: string;
  message?: string | null;
  shelterId?: string | null;
}

/**
 * Persist a volunteer application (public — no login required).
 * Returns true on success.
 */
export const createVolunteerApplication = async (input: CreateVolunteerApplicationInput): Promise<boolean> => {
  if (!isSupabaseConfigured()) {
    toast.success('Demo mód: Önkéntes jelentkezés rögzítve!');
    return true;
  }

  try {
    const { error } = await supabase.from('volunteer_applications').insert({
      name: input.name,
      email: input.email,
      phone: input.phone ?? null,
      help_type: input.helpType,
      message: input.message ?? null,
      shelter_id: input.shelterId ?? null,
    });

    if (error) throw error;
    toast.success('Köszönjük a jelentkezést! A menhely hamarosan felveszi veled a kapcsolatot.');
    return true;
  } catch (err: any) {
    if (isDev()) console.error('[createVolunteerApplication]', err);
    toast.error(`Hiba a jelentkezés során: ${err.message || 'Ismeretlen hiba'}`);
    return false;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Subscriptions
// ═══════════════════════════════════════════════════════════════════════════

export interface CreateSubscriptionInput {
  tier: string;
  monthlyAmount: number;
}

/**
 * Create a supporter subscription. Demo mode: saves to DB with demo stripe ID.
 * Stripe élesben: a Customer Portal-ra redirectál.
 */
export const createSubscription = async (input: CreateSubscriptionInput): Promise<SupporterSubscription | null> => {
  if (!isSupabaseConfigured()) {
    toast.success('Demo mód: Sikeresen feliratkoztál a havi támogatásra!');
    return {
      id: `demo-${Date.now()}`,
      userId: 'demo-user',
      tier: input.tier as any,
      monthlyAmount: input.monthlyAmount,
      stripeSubscriptionId: 'demo_sub_' + Date.now(),
      isActive: true,
      startedAt: new Date().toISOString(),
      cancelledAt: null,
    };
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    toast.error('A feliratkozáshoz be kell jelentkezned.');
    return null;
  }

  try {
    const { data: sub, error } = await supabase
      .from('supporter_subscriptions')
      .insert({
        user_id: user.id,
        tier: input.tier,
        monthly_amount: input.monthlyAmount,
        stripe_subscription_id: 'demo_sub_' + Date.now(),
        is_active: true,
      })
      .select()
      .single();

    if (error) throw error;

    toast.success('Sikeresen feliratkoztál a havi támogatásra! Köszönjük!');
    return toCamelCase<SupporterSubscription>(sub);
  } catch (err: any) {
    if (isDev()) console.error('[createSubscription]', err);
    toast.error(`Hiba a feliratkozás során: ${err.message}`);
    return null;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Shelter address (Google-validált)
// ═══════════════════════════════════════════════════════════════════════════

export interface UpdateShelterAddressInput {
  shelterId: string;
  /** A Google által visszaadott formázott cím. */
  address: string;
  lat: number;
  lng: number;
  /** Google Place ID — enélkül a cím nem jelölhető validáltnak. */
  placeId: string;
}

/**
 * A menhely címének frissítése Google-javaslatból.
 *
 * A `address_verified_at` bélyeget CSAK ez az útvonal állítja be, és csak
 * `placeId` birtokában — a DB `shelters_address_verified_complete`
 * megszorítása pedig ki is kényszeríti, hogy validáltnak jelölt címhez
 * tartozzon place_id és koordináta. Kézzel begépelt cím tehát nem tud
 * validáltnak látszani.
 *
 * A jogosultságot az RLS dönti el (`shelters_update_staff`: a saját menhely
 * Adminja vagy super_admin) — a UI csak elrejti a gombot, nem ez a védelem.
 */
export const updateShelterAddress = async (input: UpdateShelterAddressInput): Promise<boolean> => {
  if (!isSupabaseConfigured()) {
    toast.info('Demo mód — a menhely címe helyileg frissítve.');
    return true;
  }
  if (!input.placeId) {
    toast.error('Válaszd ki a címet a Google javaslatai közül.');
    return false;
  }

  try {
    const { data, error } = await supabase
      .from('shelters')
      .update({
        location: input.address,
        lat: input.lat,
        lng: input.lng,
        address_place_id: input.placeId,
        address_verified_at: new Date().toISOString(),
      })
      .eq('id', input.shelterId)
      .select('id')
      .maybeSingle();

    if (error) throw error;
    if (!data) {
      // Nincs sor: az RLS nem engedte. Ez nem "nem található" — a menhely
      // létezik, csak nincs jogosultság a módosításához.
      toast.error('Nincs jogosultságod a menhely címének módosításához.');
      return false;
    }
    toast.success('A menhely címe frissítve és validálva!');
    return true;
  } catch (err: any) {
    if (isDev()) console.error('[updateShelterAddress]', err);
    toast.error(`Hiba a cím mentése során: ${err.message}`);
    return false;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Adopter Profiles
// ═══════════════════════════════════════════════════════════════════════════

import type { AdopterProfile, LifestyleProfile } from './types';

export interface UpsertAdopterProfileInput {
  housingType?: string | null;
  housingDetails?: string | null;
  householdMembers?: number | null;
  hasChildren?: boolean | null;
  childrenAges?: string | null;
  hasOtherPets?: boolean | null;
  otherPetsDetails?: string | null;
  dailyRoutine?: string | null;
  workSchedule?: string | null;
  previousPetExperience?: boolean | null;
  previousPetDetails?: string | null;
  hasVeterinarian?: boolean | null;
  financialBackground?: string | null;
  familyPlans?: string | null;
  plannedAnimalFunction?: string | null;
  idCardNumber?: string | null;

  // Smart Match életmód-válaszok — a matcher és a profil ugyanide ír.
  lifestyleHousing?: string | null;
  lifestyleActivity?: string | null;
  lifestyleExperience?: string | null;
  lifestyleOtherPets?: string | null;
  lifestyleKids?: string | null;

  // Lakóhely (település szintű) + vállalt távolság a match pontozáshoz.
  homeCity?: string | null;
  homeLat?: number | null;
  homeLng?: number | null;
  homePlaceId?: string | null;
  maxTravelDistanceKm?: number | null;
}

/**
 * A Smart Match futtatása után csendben elmenti az életmód-válaszokat a
 * profilba, hogy legközelebb ne kelljen újra végigkattintani őket.
 *
 * Külön függvény, mert NEM a felhasználó kérte a mentést: nem dobunk sikeres
 * toastot (az zavaró lenne a találatok megjelenésekor), és a hiba sem
 * szakítja meg a matchinget — a találatokat mindenképp látnia kell.
 */
export const saveLifestyleToProfile = async (profile: LifestyleProfile): Promise<void> => {
  if (!isSupabaseConfigured()) return;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return; // be nem jelentkezett user: a böngészőben marad (matchSession)

  try {
    const { error } = await supabase
      .from('adopter_profiles')
      .upsert({
        id: user.id,
        lifestyle_housing: profile.housing || null,
        lifestyle_activity: profile.activity || null,
        lifestyle_experience: profile.experience || null,
        lifestyle_other_pets: profile.otherPets || null,
        lifestyle_kids: profile.kids || null,
        // A kérdőív átfedő logikai mezőit is szinkronban tartjuk, hogy az
        // örökbefogadási igény űrlapja is profitáljon a matching válaszaiból.
        has_children: profile.kids ? profile.kids !== 'none' : null,
        has_other_pets: profile.otherPets ? profile.otherPets !== 'none' : null,
        previous_pet_experience: profile.experience ? profile.experience === 'experienced' : null,
      }, { onConflict: 'id' });
    if (error) throw error;
  } catch (err: any) {
    if (isDev()) console.warn('[saveLifestyleToProfile]', err.message);
  }
};

/**
 * Upsert adopter profile. Creates if not exists, updates otherwise.
 * Returns the profile or null on failure.
 */
export const upsertAdopterProfile = async (input: UpsertAdopterProfileInput): Promise<AdopterProfile | null> => {
  if (!isSupabaseConfigured()) {
    toast.success('Demo mód: Profil mentve!');
    return {
      id: 'demo-user',
      housingType: input.housingType ?? null,
      housingDetails: input.housingDetails ?? null,
      householdMembers: input.householdMembers ?? null,
      hasChildren: input.hasChildren ?? null,
      childrenAges: input.childrenAges ?? null,
      hasOtherPets: input.hasOtherPets ?? null,
      otherPetsDetails: input.otherPetsDetails ?? null,
      dailyRoutine: input.dailyRoutine ?? null,
      workSchedule: input.workSchedule ?? null,
      previousPetExperience: input.previousPetExperience ?? null,
      previousPetDetails: input.previousPetDetails ?? null,
      hasVeterinarian: input.hasVeterinarian ?? null,
      financialBackground: input.financialBackground ?? null,
      familyPlans: input.familyPlans ?? null,
      plannedAnimalFunction: input.plannedAnimalFunction ?? null,
      idCardNumber: input.idCardNumber ?? null,
      lifestyleHousing: input.lifestyleHousing ?? null,
      lifestyleActivity: input.lifestyleActivity ?? null,
      lifestyleExperience: input.lifestyleExperience ?? null,
      lifestyleOtherPets: input.lifestyleOtherPets ?? null,
      lifestyleKids: input.lifestyleKids ?? null,
      homeCity: input.homeCity ?? null,
      homeLat: input.homeLat ?? null,
      homeLng: input.homeLng ?? null,
      homePlaceId: input.homePlaceId ?? null,
      maxTravelDistanceKm: input.maxTravelDistanceKm ?? null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    toast.error('A profil mentéséhez be kell jelentkezned.');
    return null;
  }

  try {
    const snakeInput: Record<string, any> = { id: user.id };
    if (input.housingType !== undefined) snakeInput.housing_type = input.housingType;
    if (input.housingDetails !== undefined) snakeInput.housing_details = input.housingDetails;
    if (input.householdMembers !== undefined) snakeInput.household_members = input.householdMembers;
    if (input.hasChildren !== undefined) snakeInput.has_children = input.hasChildren;
    if (input.childrenAges !== undefined) snakeInput.children_ages = input.childrenAges;
    if (input.hasOtherPets !== undefined) snakeInput.has_other_pets = input.hasOtherPets;
    if (input.otherPetsDetails !== undefined) snakeInput.other_pets_details = input.otherPetsDetails;
    if (input.dailyRoutine !== undefined) snakeInput.daily_routine = input.dailyRoutine;
    if (input.workSchedule !== undefined) snakeInput.work_schedule = input.workSchedule;
    if (input.previousPetExperience !== undefined) snakeInput.previous_pet_experience = input.previousPetExperience;
    if (input.previousPetDetails !== undefined) snakeInput.previous_pet_details = input.previousPetDetails;
    if (input.hasVeterinarian !== undefined) snakeInput.has_veterinarian = input.hasVeterinarian;
    if (input.financialBackground !== undefined) snakeInput.financial_background = input.financialBackground;
    if (input.familyPlans !== undefined) snakeInput.family_plans = input.familyPlans;
    if (input.plannedAnimalFunction !== undefined) snakeInput.planned_animal_function = input.plannedAnimalFunction;
    if (input.idCardNumber !== undefined) snakeInput.id_card_number = input.idCardNumber;
    if (input.lifestyleHousing !== undefined) snakeInput.lifestyle_housing = input.lifestyleHousing;
    if (input.lifestyleActivity !== undefined) snakeInput.lifestyle_activity = input.lifestyleActivity;
    if (input.lifestyleExperience !== undefined) snakeInput.lifestyle_experience = input.lifestyleExperience;
    if (input.lifestyleOtherPets !== undefined) snakeInput.lifestyle_other_pets = input.lifestyleOtherPets;
    if (input.lifestyleKids !== undefined) snakeInput.lifestyle_kids = input.lifestyleKids;
    if (input.homeCity !== undefined) snakeInput.home_city = input.homeCity;
    if (input.homeLat !== undefined) snakeInput.home_lat = input.homeLat;
    if (input.homeLng !== undefined) snakeInput.home_lng = input.homeLng;
    if (input.homePlaceId !== undefined) snakeInput.home_place_id = input.homePlaceId;
    if (input.maxTravelDistanceKm !== undefined) snakeInput.max_travel_distance_km = input.maxTravelDistanceKm;

    const { data, error } = await supabase
      .from('adopter_profiles')
      .upsert(snakeInput, { onConflict: 'id' })
      .select()
      .single();

    if (error) throw error;
    toast.success('Profil sikeresen mentve!');
    return toCamelCase<AdopterProfile>(data);
  } catch (err: any) {
    if (isDev()) console.error('[upsertAdopterProfile]', err);
    toast.error(`Hiba a profil mentése során: ${err.message}`);
    return null;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Shelter Adoption Policies
// ═══════════════════════════════════════════════════════════════════════════

import type { ShelterAdoptionPolicy } from './types';

export interface UpsertShelterPolicyInput {
  shelterId: string;
  defaultRequiredFields?: string[];
  requiresMultipleVisits?: boolean;
  requiresHomeCheck?: boolean;
  requiresSpayNeuter?: boolean;
  spayNeuterDeadlineDays?: number | null;
  contractType?: 'szigorú' | 'egyszerű';
  requiresPhotoReports?: boolean;
  photoReportFrequency?: string | null;
  allowsThirdPartyTransfer?: boolean;
  defaultAdoptionFee?: number;
  policyNotes?: string | null;
}

/**
 * Upsert a shelter's adoption policy. Creates if not exists, updates otherwise.
 */
export const upsertShelterPolicy = async (input: UpsertShelterPolicyInput): Promise<ShelterAdoptionPolicy | null> => {
  if (!isSupabaseConfigured()) {
    toast.success('Demo mód: Menhelyi szabályzat mentve!');
    return {
      id: `demo-policy-${Date.now()}`,
      shelterId: input.shelterId,
      defaultRequiredFields: input.defaultRequiredFields ?? [],
      requiresMultipleVisits: input.requiresMultipleVisits ?? false,
      requiresHomeCheck: input.requiresHomeCheck ?? false,
      requiresSpayNeuter: input.requiresSpayNeuter ?? true,
      spayNeuterDeadlineDays: input.spayNeuterDeadlineDays ?? null,
      contractType: input.contractType ?? 'szigorú',
      requiresPhotoReports: input.requiresPhotoReports ?? true,
      photoReportFrequency: input.photoReportFrequency ?? '3_hónap',
      allowsThirdPartyTransfer: input.allowsThirdPartyTransfer ?? false,
      defaultAdoptionFee: input.defaultAdoptionFee ?? 15000,
      policyNotes: input.policyNotes ?? null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  try {
    const { data, error } = await supabase
      .from('shelter_adoption_policies')
      .upsert({
        shelter_id: input.shelterId,
        default_required_fields: input.defaultRequiredFields ?? [],
        requires_multiple_visits: input.requiresMultipleVisits ?? false,
        requires_home_check: input.requiresHomeCheck ?? false,
        requires_spay_neuter: input.requiresSpayNeuter ?? true,
        spay_neuter_deadline_days: input.spayNeuterDeadlineDays ?? null,
        contract_type: input.contractType ?? 'szigorú',
        requires_photo_reports: input.requiresPhotoReports ?? true,
        photo_report_frequency: input.photoReportFrequency ?? '3_hónap',
        allows_third_party_transfer: input.allowsThirdPartyTransfer ?? false,
        default_adoption_fee: input.defaultAdoptionFee ?? 15000,
        policy_notes: input.policyNotes ?? null,
      }, { onConflict: 'shelter_id' })
      .select()
      .single();

    if (error) throw error;
    toast.success('Menhelyi örökbefogadási szabályzat mentve!');
    return toCamelCase<ShelterAdoptionPolicy>(data);
  } catch (err: any) {
    if (isDev()) console.error('[upsertShelterPolicy]', err);
    toast.error(`Hiba a szabályzat mentése során: ${err.message}`);
    return null;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Shelter Notification Settings — snooze duration + per-type lead times
// ═══════════════════════════════════════════════════════════════════════════

import type { ShelterNotificationSettings } from './types';

export interface UpsertShelterNotificationSettingsInput {
  shelterId: string;
  snoozeDurationMinutes?: number;
  leadDaysByType?: Partial<Record<MedicalEventType, number>>;
}

/** Upsert a shelter's notification settings (snooze duration, per-event-type lead days). */
export const upsertShelterNotificationSettings = async (
  input: UpsertShelterNotificationSettingsInput
): Promise<ShelterNotificationSettings | null> => {
  if (!isSupabaseConfigured()) {
    toast.success('Demo mód: Értesítési beállítások mentve!');
    return {
      id: `demo-notification-settings-${Date.now()}`,
      shelterId: input.shelterId,
      snoozeDurationMinutes: input.snoozeDurationMinutes ?? 60,
      leadDaysByType: input.leadDaysByType ?? {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  try {
    const snakeInput: Record<string, any> = { shelter_id: input.shelterId };
    if (input.snoozeDurationMinutes !== undefined) snakeInput.snooze_duration_minutes = input.snoozeDurationMinutes;
    if (input.leadDaysByType !== undefined) snakeInput.lead_days_by_type = input.leadDaysByType;

    const { data, error } = await supabase
      .from('shelter_notification_settings')
      .upsert(snakeInput, { onConflict: 'shelter_id' })
      .select()
      .single();

    if (error) throw error;
    toast.success('Értesítési beállítások mentve!');
    return toCamelCase<ShelterNotificationSettings>(data);
  } catch (err: any) {
    if (isDev()) console.error('[upsertShelterNotificationSettings]', err);
    toast.error(`Hiba a beállítások mentése során: ${err.message}`);
    return null;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Multi-step Adoption Flow
// ═══════════════════════════════════════════════════════════════════════════

/** Step 1: Adopter submits adoption request → animal: AdoptionPending, lead: Pending */
export const submitAdoptionRequest = async (animalId: string, adopterId: string, message?: string): Promise<{ success: boolean; leadId?: string; error?: string; message?: string }> => {
  if (!isSupabaseConfigured()) {
    toast.success('Demo mód: Örökbefogadási igény elküldve!');
    return { success: true, message: 'Demo mód: Igény szimulálva.' };
  }

  try {
    const { data, error } = await supabase.rpc('submit_adoption_request', {
      p_animal_id: animalId,
      p_adopter_id: adopterId,
      p_message: message ?? null,
    });

    if (error) throw error;
    const result = data as any;
    if (result.success) {
      toast.success('Örökbefogadási igény elküldve! A menhely hamarosan elbírálja.');
    } else {
      toast.error(result.error || 'Hiba az igénylés elküldése során.');
    }
    return result;
  } catch (err: any) {
    if (isDev()) console.error('[submitAdoptionRequest]', err);
    toast.error(`Hiba: ${err.message}`);
    return { success: false, error: err.message };
  }
};

/**
 * 2. lépés (menhely): az örökbefogadási igény elbírálása.
 *
 * A `reviewNote` az ELFOGADÁSHOZ is kötelező, nem csak az elutasításhoz — az
 * örökbefogadó számára ez az egyetlen visszajelzés arról, miért született így
 * a döntés. A kötelezőséget a szerver érvényesíti (review_adoption); a felület
 * csak megelőzi a fölösleges kört.
 *
 * A `contact` művelet nem bírálat, csak az elérhetőség felfedése — ott nincs
 * indoklás.
 */
export const reviewAdoption = async (
  leadId: string,
  action: 'accept' | 'reject' | 'contact',
  reviewNote?: string,
  staffId?: string
): Promise<{ success: boolean; action?: string; adopterEmail?: string; adopterPhone?: string; error?: string; message?: string }> => {
  if (!isSupabaseConfigured()) {
    toast.success(`Demo mód: ${action === 'accept' ? 'Elfogadva' : action === 'reject' ? 'Elutasítva' : 'Kapcsolatfelvétel'}!`);
    return { success: true, action };
  }

  try {
    const { data, error } = await supabase.rpc('review_adoption', {
      p_lead_id: leadId,
      p_action: action,
      p_review_note: reviewNote ?? null,
      p_staff_id: staffId ?? null,
    });

    if (error) throw error;
    const result = data as any;
    if (result.success) {
      const labels: Record<string, string> = { accepted: 'Igénylés elfogadva!', rejected: 'Igénylés elutasítva.', contacted: 'Kapcsolatfelvétel kezdeményezve.' };
      toast.success(labels[result.action] || 'Művelet sikeres.');
    } else {
      toast.error(result.error || 'Hiba a művelet során.');
    }
    return result;
  } catch (err: any) {
    if (isDev()) console.error('[reviewAdoption]', err);
    toast.error(`Hiba: ${err.message}`);
    return { success: false, error: err.message };
  }
};

/** Step 3 (Adopter): Finalize adoption after shelter acceptance */
export const finalizeAdoption = async (input: {
  animalId: string;
  adopterId: string;
  adoptionFee?: number;
  extraDonation?: number;
  insurancePlan?: string | null;
  contractSummary?: string | null;
  signatureData?: string | null;
}): Promise<{ success: boolean; contractId?: string; transactionId?: string; error?: string; message?: string }> => {
  if (!isSupabaseConfigured()) {
    toast.success('Demo mód: Örökbefogadás véglegesítve! 🎉');
    return { success: true, message: 'Demo mód: Véglegesítés szimulálva.' };
  }

  try {
    const { data, error } = await supabase.rpc('finalize_adoption', {
      p_animal_id: input.animalId,
      p_adopter_id: input.adopterId,
      p_adoption_fee: input.adoptionFee ?? 15000,
      p_extra_donation: input.extraDonation ?? 0,
      p_insurance_plan: input.insurancePlan ?? null,
      p_contract_summary: input.contractSummary ?? null,
      p_signature_data: input.signatureData ?? null,
    });

    if (error) throw error;
    const result = data as any;
    if (result.success) {
      toast.success(result.message || 'Örökbefogadás véglegesítve! 🎉');
    } else {
      toast.error(result.error || 'Hiba a véglegesítés során.');
    }
    return result;
  } catch (err: any) {
    if (isDev()) console.error('[finalizeAdoption]', err);
    toast.error(`Hiba: ${err.message}`);
    return { success: false, error: err.message };
  }
};
